/**
 * @namespace transat/remote-work
 */
import { concatAll } from '~/utils/lib/core/prelude';

/**
 * @typedef {object} RemoteRequest
 * @property {string} request_id primary key
 * @property {string} date ISO 8601
 * @property {PERIOD} period
 * @property {STATUS} status
 * @property {RemoteRequestRejectionInfo | null} rejection_info
 * @property {string} last_status_update ISO 8601
 *
 *
 * @typedef {object} RemoteRequestRejectionInfo
 * @property {string} reason
 *
 *
 * @typedef {object} SingleUpdateResponseData
 * @property {string} message
 * @property {STATUS} status
 * @property {number} days - integer gte 0
 * @property {string} last_status_update - ISO 8601
 *
 *
 * @typedef {object} RemoteRequestSalarie
 * @property {string} id primary key
 * @property {string} nom
 * @property {string} prenom
 * @property {string} matricule unique
 * @property {string} mail
 *
 *
 * @typedef {object} GroupedRemoteRequest
 * @property {string} grouped_request_id PK
 * @property {string | null} approver string or null if added_by_employer
 * @property {boolean} added_by_employer
 * @property {string} comment
 * @property {RemoteRequestSalarie} salarie
 * @property {string} created_at ISO 8601
 * @property {string} from ISO 8601
 * @property {string} to ISO 8601
 * @property {number} days float, changes based on canceled requests in requests array
 * @property {Array<RemoteRequest>} requests
 */

/**
 * Remote work "fake" leave type
 * Left out of the LEAVE_TYPE_ enum to avoid confusion with the actual leave types
 */
export const REMOTE_WORK_LEAVE_TYPE = 'remote-work'

/**
 * @enum {typeof EVENTS[keyof typeof EVENTS]}
 * @readonly
 */
export const EVENTS = /** @type {const} */ ({
  ON_PENDING_LIST_REFRESH_NEEDED: 'on_pending_list_refresh_needed',
  ON_ARCHIVED_LIST_REFRESH_NEEDED: 'on_archived_list_refresh_needed',
})

/**
 * @enum {typeof PERIOD[keyof typeof PERIOD]}
 * @readonly
 */
export const PERIOD = /** @type {const} */ ({
  AM: 'AM',
  PM: 'PM',
  FULL: 'FULL',
})

/**
 * @enum {typeof STATUS[keyof typeof STATUS]}
 * @readonly
 */
export const STATUS = /** @type {const} */ ({
  WAITING: 'waiting',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  OVERRIDDEN_BY_LEAVE: 'overridden_by_leave',
  CANCELED_BY_EMPLOYER: 'canceled_by_employer',
  CANCELED_BY_EMPLOYEE: 'canceled_by_employee',
})

/** @type {(status: STATUS) => string} */
export const printStatus = status => {
  switch (status) {
    case STATUS.WAITING:  return 'En attente'
    case STATUS.ACCEPTED:   return 'Accepté'
    case STATUS.REJECTED:  return 'Rejeté'
    case STATUS.OVERRIDDEN_BY_LEAVE:  return 'Annulé par un congé'
    case STATUS.CANCELED_BY_EMPLOYER: return `Annulé par l'employeur`;
    case STATUS.CANCELED_BY_EMPLOYEE: return `Annulé par l'employé`;
  }
}

/**
 * @type {(status: STATUS) => string}
 * @desc Best used with `UI.ICON_THEME.FILLED`
 */
export const getStatusIcon = status => {
  switch (status) {
    case STATUS.WAITING:  return 'fiber_manual_record'
    case STATUS.ACCEPTED:   return 'done'
    case STATUS.REJECTED:  return 'close'
    case STATUS.OVERRIDDEN_BY_LEAVE:  return 'block'
    case STATUS.CANCELED_BY_EMPLOYER: return 'block'
    case STATUS.CANCELED_BY_EMPLOYEE: return 'block'
  }
}

/** @type {(period: PERIOD) => string} */
export const printPeriod = period => {
  switch (period) {
    case PERIOD.AM: return 'Une matinée'
    case PERIOD.PM: return 'Une après-midi'
    case PERIOD.FULL: return 'Une journée'
    default: return period
  }
}
/** @type {(period: PERIOD) => string} */
export const printPeriod_ = period => {
  switch (period) {
    case PERIOD.AM: return 'Matinée'
    case PERIOD.PM: return 'Après-midi'
    case PERIOD.FULL: return 'Journée'
    default: return period
  }
}

export const getMainRoute = (
  /** @type {import('~/stores/client/access').Structure["id"]} */ structure_id,
  /** @type {import('~/stores/client/access').Company["code_societe"]} */ code_societe,
  ) => concatAll([
    `/data/${structure_id}`,
    `/conges`,
    `/remote-work`,
    `?company=${encodeURIComponent(code_societe)}`
  ])
